// Wealth → CFO: the pieces shared by the chat and the library — folder picker, a file waiting to be filed, a fact row, the library
import clsx from "clsx";
import { Check, Cloud, Download, FileText, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { useRef, useState } from "react";
import { cfo, cfoErr, downloadUrl, fmtOnDay, fmtSize, type CfoFact, type CfoFile, type CfoState } from "./cfoApi";

export const iconBtn = "inline-grid h-7 w-7 shrink-0 place-items-center rounded-md text-[var(--muted)] hover:bg-[rgb(11_12_42/0.05)] hover:text-[var(--text)]";
export const iconDanger = `${iconBtn} hover:!text-red-600`;
export const mini = "btn btn-ghost btn-sm !min-h-0 !px-2 !py-0.5 !text-xs";
export const sideLabel = "section-label mb-0.5 mt-2.5";
const fieldLabel = "flex min-w-0 flex-1 basis-full flex-col gap-0.5 text-[11px] text-[var(--muted)]";
const smallInput = "input !min-h-0 !rounded-md !px-2 !py-1 !text-[0.8125rem]";

// Every folder AND every parent of one (so "02 Companies/<company>" itself can be chosen), plus the value in hand if it is new
const folderTree = (folders: string[], extra?: string) => {
  const all = new Set<string>();
  for (const p of [...folders, ...(extra ? [extra] : [])]) {
    const segs = String(p).split("/").filter(Boolean);
    for (let i = 1; i <= segs.length; i++) all.add(segs.slice(0, i).join("/"));
  }
  return [...all].sort((a, b) => a.localeCompare(b));
};
const NB = "   ";

export function FolderPicker({ value, folders, onChange }: { value: string; folders: string[]; onChange: (v: string) => void }) {
  return (
    <select
      className={clsx(smallInput, "w-full")}
      value={value}
      title={value}
      onChange={(e) => {
        if (e.target.value !== "__new__") return onChange(e.target.value);
        const name = window.prompt(`New folder inside "${value}"`, "");
        const clean = String(name || "").replace(/[\\/:*?"<>|]/g, " ").replace(/\s+/g, " ").trim();
        if (clean) onChange(`${value}/${clean}`);
      }}
    >
      {folderTree(folders, value).map((p) => {
        const segs = p.split("/");
        // the chosen one shows its whole path (what the closed box displays); the rest are indented by depth
        return <option key={p} value={p}>{p === value ? segs.join("  ›  ") : `${NB.repeat(segs.length - 1)}${segs[segs.length - 1]}`}</option>;
      })}
      <option value="__new__">+ New folder inside the one chosen…</option>
    </select>
  );
}

const fileBox = "my-0.5 flex max-w-full items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 py-1.5 text-[0.8125rem]";

/** A file in a message or the library: reading, filed, or waiting with its proposed folder and name. */
export function FileCard({ f, folders, onChanged, onDelete }: { f: CfoFile; folders: string[]; onChanged: () => void; onDelete: (f: CfoFile) => void }) {
  const [d, setD] = useState<{ folder?: string; name?: string }>({});
  if (f.status === "reading")
    return (
      <div className={clsx(fileBox, "border-dashed")}>
        <Loader2 size={14} className="shrink-0 animate-spin" /><span className="min-w-0 flex-1 truncate">{f.origName}</span><span className="muted shrink-0 text-xs">reading… {fmtSize(f.size)}</span>
      </div>
    );
  if (f.status === "filed")
    return (
      <div className={fileBox}>
        <FileText size={14} className="shrink-0" /><span className="min-w-0 flex-1 truncate" title={f.origName}>{f.name}</span>
        <span className="muted max-w-[45%] truncate text-xs">{f.folder}</span>
        {f.drive?.link && f.drive.current && <a className={iconBtn} href={f.drive.link} target="_blank" rel="noreferrer" title="Open the copy in Google Drive"><Cloud size={13} /></a>}
        <a className={iconBtn} href={downloadUrl(f.id)} download title="Download"><Download size={13} /></a>
      </div>
    );
  const folder = d.folder ?? f.proposal?.folder ?? "00 Inbox";
  const name = d.name ?? f.proposal?.name ?? f.origName;
  const fileIt = (where: { folder: string; name: string }) => cfo.fileIt(f.id, where).then(() => { setD({}); onChanged(); }).catch((e) => window.alert(cfoErr(e)));
  return (
    <div className="my-1 w-full max-w-[640px] rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[0.8125rem]">
      <div className="flex items-center gap-2">
        <FileText size={14} className="shrink-0" /><span className="min-w-0 flex-1 truncate">{f.origName}</span>
        <span className="muted shrink-0 text-xs">{f.textChars ? `${f.textChars.toLocaleString()} characters read${f.method?.includes("ocr") ? " by OCR" : ""}` : "no readable text"} · {fmtSize(f.size)}</span>
      </div>
      {f.duplicateOf && <p className="my-1 text-amber-800">The same file is already in the library as "{f.duplicateOf}".</p>}
      {f.summary && <p className="my-1.5 text-[var(--text-2)]">{f.summary}</p>}
      <div className="flex flex-wrap items-end gap-2">
        <label className={fieldLabel}>Folder<FolderPicker value={folder} folders={folders} onChange={(v) => setD((x) => ({ ...x, folder: v }))} /></label>
        <label className={fieldLabel}>Name<input className={smallInput} value={name} title={name} onChange={(e) => setD((x) => ({ ...x, name: e.target.value }))} /></label>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <button className="btn btn-primary btn-sm" onClick={() => void fileIt({ folder, name })}><Check size={13} /> File it</button>
        <button className="btn btn-ghost btn-sm" onClick={() => void fileIt({ folder: "00 Inbox", name: f.origName })}>Keep in Inbox</button>
        <button className={iconDanger} title="Delete the file" onClick={() => onDelete(f)}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

/** One fact: proposed ones can be remembered or dismissed; any can be corrected. */
export function FactRow({ k, onDecide, onChanged }: { k: CfoFact; onDecide: (ids: string[], yes: boolean) => void; onChanged: () => void }) {
  const [edit, setEdit] = useState<string | null>(null);
  const save = () => cfo.patchFact(k.id, { text: edit ?? k.text }).then(() => { setEdit(null); onChanged(); }).catch((e) => window.alert(cfoErr(e)));
  return (
    <div className={clsx("flex items-center gap-1.5 text-[0.8125rem]", k.status === "proposed" ? "my-1 rounded-md bg-amber-50 px-2 py-1.5" : "border-t border-[var(--line)] px-1 py-1.5")}>
      {edit != null ? (
        <>
          <input className={clsx(smallInput, "min-w-0 flex-1")} value={edit} autoFocus onChange={(e) => setEdit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void save(); }} />
          <button className={iconBtn} title="Save" onClick={() => void save()}><Check size={13} /></button>
          <button className={iconBtn} title="Cancel" onClick={() => setEdit(null)}><X size={13} /></button>
        </>
      ) : (
        <>
          <div className="min-w-0 flex-1">
            <div>{k.text}</div>
            <div className="muted line-clamp-2 text-[11.5px]">
              {[k.entity, k.asOf && `as of ${k.asOf}`, k.source?.name && `from ${k.source.name}${k.source.where ? `, ${k.source.where}` : ""}`].filter(Boolean).join(" · ")}
            </div>
          </div>
          {k.status === "proposed" && <button className={mini} onClick={() => onDecide([k.id], true)}><Check size={12} /> Remember</button>}
          <button className={iconBtn} title="Correct it" onClick={() => setEdit(k.text)}><Pencil size={12} /></button>
          <button
            className={iconDanger}
            title={k.status === "proposed" ? "Not worth keeping" : "Forget this"}
            onClick={() => {
              if (k.status === "proposed") onDecide([k.id], false);
              else if (window.confirm("Forget this fact?")) cfo.deleteFact(k.id).then(onChanged).catch((e) => window.alert(cfoErr(e)));
            }}
          >
            <X size={13} />
          </button>
        </>
      )}
    </div>
  );
}

/** Library: folder tree, files waiting to be filed, filed files (move, rename, download, delete), the Drive mirror. */
export function Library({ state, busy, err, upload, onChanged, onDelete }: {
  state: CfoState; busy: string; err: string; upload: (list: FileList) => void; onChanged: () => void; onDelete: (f: CfoFile) => void;
}) {
  const [filter, setFilter] = useState("");
  const [moving, setMoving] = useState<string | null>(null);
  const [move, setMove] = useState<{ folder: string; name: string }>({ folder: "", name: "" });
  const fileRef = useRef<HTMLInputElement>(null);
  const files = state.files;
  const folders = state.folders || [];
  const drive = state.drive;
  const toFile = files.filter((f) => f.status === "proposed");
  const tops = [...new Set(folders.map((f) => f.split("/")[0]!))];
  const inFolder = files.filter((f) => f.status !== "reading" && (!filter || f.folder === filter || f.folder.startsWith(`${filter}/`)));
  const filed = inFolder.filter((f) => f.status === "filed");
  const countIn = (p: string) => files.filter((f) => f.folder === p || f.folder.startsWith(`${p}/`)).length;
  const treeBtn = (on: boolean, extra?: string) =>
    clsx("flex w-full justify-between gap-1.5 rounded-md px-2 py-1 text-left text-[0.8125rem]", on ? "bg-brand-50 font-semibold text-brand-600" : "text-[var(--text-2)] hover:bg-[var(--panel-2)]", extra);
  const count = (n: number) => <span className="text-[11.5px] font-normal text-[var(--subtle)]">{n || ""}</span>;

  return (
    <div className="grid min-h-[520px] grid-cols-1 sm:grid-cols-[240px_minmax(0,1fr)]">
      <div className="scroll-thin flex max-h-[200px] flex-col gap-px overflow-y-auto border-b border-[var(--line)] p-2.5 sm:max-h-[calc(100vh-170px)] sm:border-b-0 sm:border-r">
        <button className={treeBtn(!filter)} onClick={() => setFilter("")}>All files {count(files.length)}</button>
        {tops.map((t) => (
          <div key={t}>
            <button className={treeBtn(filter === t)} onClick={() => setFilter(t)}>{t} {count(countIn(t))}</button>
            {(filter === t || filter.startsWith(`${t}/`)) &&
              [...new Set(folders.filter((f) => f.startsWith(`${t}/`)).map((f) => f.split("/").slice(0, 2).join("/")))].map((sub) => (
                <div key={sub}>
                  <button className={treeBtn(filter === sub, "pl-[18px]")} onClick={() => setFilter(sub)}>{sub.split("/")[1]} {count(countIn(sub))}</button>
                  {(filter === sub || filter.startsWith(`${sub}/`)) &&
                    folders.filter((f) => f.startsWith(`${sub}/`)).map((f) => (
                      <button key={f} className={treeBtn(filter === f, "pl-[30px] !text-xs")} onClick={() => setFilter(f)}>{f.split("/").slice(2).join(" / ")} {count(countIn(f))}</button>
                    ))}
                </div>
              ))}
          </div>
        ))}
      </div>
      <div className="min-w-0 px-3.5 py-3">
        <div className="mb-2 flex items-center justify-between gap-2 text-sm">
          <strong className="min-w-0 truncate">{filter || "All files"}</strong>
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => { if (e.target.files) upload(e.target.files); e.target.value = ""; }} />
          <button className="btn btn-ghost btn-sm" disabled={busy === "upload"} onClick={() => fileRef.current?.click()}>
            {busy === "upload" ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add files
          </button>
        </div>
        {err && <p className="mb-1 text-sm text-red-600">{err}</p>}
        {files.filter((f) => f.status === "reading").map((f) => <FileCard key={f.id} f={f} folders={folders} onChanged={onChanged} onDelete={onDelete} />)}
        {toFile.length > 0 && !filter && (
          <>
            <div className={sideLabel}>Waiting for you to file</div>
            {toFile.map((f) => <FileCard key={f.id} f={f} folders={folders} onChanged={onChanged} onDelete={onDelete} />)}
          </>
        )}
        {filed.map((f) => (
          <div key={f.id} className={clsx(fileBox, "my-1 flex-wrap items-start")}>
            <FileText size={14} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="truncate" title={f.origName}>{f.name}</div>
              <div className="muted line-clamp-2 text-[11.5px]">
                {f.folder} · {fmtSize(f.size)} · {fmtOnDay(f.uploadedAt)}
                {drive.connected ? (f.drive?.current ? " · on Drive" : f.drive?.error ? " · Drive copy failed" : " · waiting for Drive") : ""}
                {f.summary ? ` · ${f.summary}` : ""}
              </div>
            </div>
            {f.drive?.link && f.drive.current && <a className={iconBtn} href={f.drive.link} target="_blank" rel="noreferrer" title="Open the copy in Google Drive"><Cloud size={13} /></a>}
            <a className={iconBtn} href={downloadUrl(f.id)} download title="Download"><Download size={13} /></a>
            <button className={iconBtn} title="Move or rename" onClick={() => { setMoving(moving === f.id ? null : f.id); setMove({ folder: f.folder, name: f.name }); }}><Pencil size={12} /></button>
            <button className={iconDanger} title="Delete" onClick={() => onDelete(f)}><Trash2 size={13} /></button>
            {moving === f.id && (
              <div className="mt-1.5 basis-full border-t border-dashed border-[var(--line)] pt-1.5">
                <div className="flex flex-wrap items-end gap-2">
                  <label className={fieldLabel}>Move to<FolderPicker value={move.folder} folders={folders} onChange={(v) => setMove((x) => ({ ...x, folder: v }))} /></label>
                  <label className={fieldLabel}>Name<input className={smallInput} value={move.name} onChange={(e) => setMove((x) => ({ ...x, name: e.target.value }))} /></label>
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  <button className="btn btn-primary btn-sm" onClick={() => cfo.fileIt(f.id, move).then(() => { setMoving(null); onChanged(); }).catch((e) => window.alert(cfoErr(e)))}><Check size={13} /> Save</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setMoving(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!filed.length && !(toFile.length && !filter) && <p className="muted py-4 text-sm">Nothing filed here yet. Add files, or drop them into a conversation.</p>}
        <p className={clsx("mt-2.5 text-xs", drive.needsSignIn || drive.quotaFull ? "text-red-600" : "muted")}>
          {drive.connected
            ? drive.note || (
                <>
                  Copied to Google Drive under {drive.rootLink ? <a className="underline" href={drive.rootLink} target="_blank" rel="noreferrer">{drive.root}</a> : drive.root} ({drive.account}): {drive.mirrored} file{drive.mirrored === 1 ? "" : "s"}
                  {drive.pending ? `, ${drive.pending} waiting` : ""}. Removing a file here moves its Drive copy to 99 Archive; nothing is deleted there.
                </>
              )
            : drive.note}
          {drive.connected && drive.pending > 0 && !drive.needsSignIn && !drive.quotaFull && (
            <> <button className={mini} onClick={() => cfo.driveSync().then(onChanged).catch((e) => window.alert(cfoErr(e)))}>Copy to Drive now</button></>
          )}
        </p>
      </div>
    </div>
  );
}
